import React, { useState } from 'react';
import { Modal, Form, Input, Select, Upload, Switch, message, Tag } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { CreateAudioRequest } from '../../types';

const { Option } = Select;
const { Dragger } = Upload;
const { TextArea } = Input;

interface UploadAudioModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultType?: 'music' | 'sound_effect';
}

export const UploadAudioModal: React.FC<UploadAudioModalProps> = ({
  visible,
  onClose,
  onSuccess,
  defaultType
}) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (fileList.length === 0) {
        message.error('Please select an audio file');
        return;
      }

      setUploading(true);

      const file = fileList[0].originFileObj as File;
      const data: CreateAudioRequest = {
        type: values.type,
        name: values.name,
        description: values.description,
        category: values.category,
        tags: values.tags || [],
        analyzeWithAI: values.analyzeWithAI || false
      };

      await audioLibraryApi.createAudio(file, data);
      
      message.success(
        data.analyzeWithAI 
          ? 'Audio uploaded successfully. AI analysis in progress...' 
          : 'Audio uploaded successfully'
      );
      
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to upload audio: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file: File) => {
      const isAudio = file.type.startsWith('audio/');
      if (!isAudio) {
        message.error('You can only upload audio files!');
        return Upload.LIST_IGNORE;
      }
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        message.error('Audio file must be smaller than 50MB!');
        return Upload.LIST_IGNORE;
      }
      setFileList([{
        uid: String(Date.now()),
        name: file.name,
        status: 'done',
        originFileObj: file,
      } as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    }
  };

  const musicCategories = ['cinematic', 'orchestral', 'ambient', 'electronic', 'acoustic', 'other'];
  const sfxCategories = ['transition', 'impact', 'ambient', 'emotion', 'action'];

  const selectedType = Form.useWatch('type', form);

  return (
    <Modal
      title="Upload Audio"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={uploading}
      width={600}
      okText="Upload"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: defaultType || 'music',
          analyzeWithAI: true
        }}
      >
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: 'Please select audio type' }]}
        >
          <Select>
            <Option value="music">Music</Option>
            <Option value="sound_effect">Sound Effect</Option>
          </Select>
        </Form.Item>

        <Form.Item name="file" label="Audio File">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag audio file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for MP3, WAV, M4A, OGG formats. Maximum file size: 50MB
            </p>
          </Dragger>
        </Form.Item>

        <Form.Item name="name" label="Name (Optional)">
          <Input placeholder="Leave empty to use filename" />
        </Form.Item>

        <Form.Item name="description" label="Description (Optional)">
          <TextArea rows={3} placeholder="Describe the audio" />
        </Form.Item>

        <Form.Item name="category" label="Category (Optional)">
          <Select placeholder="Select a category" allowClear>
            {(selectedType === 'music' ? musicCategories : sfxCategories).map(cat => (
              <Option key={cat} value={cat}>
                <Tag>{cat}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="tags" label="Tags (Optional)">
          <Select
            mode="tags"
            placeholder="Add tags (press Enter after each tag)"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="analyzeWithAI" label="AI Analysis" valuePropName="checked">
          <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px' }}>
            Automatically analyze audio with AI to generate name, description, and metadata
          </span>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadAudioModal;
